import React, { createContext, useMemo, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import * as Yup from 'yup';
import { findClosestMatchFromObject } from '../../utils/search-utils';
import { useUpload } from '../../use-upload';
import { importContacts } from 'shared/store/actions/contacts.actions';
import { getLabelsList } from 'shared/store/selectors/labels.selector';
import { useProfileOptions } from 'shared/hooks/profile.hooks';
import { getLang } from 'shared/store/selectors/lang.selector';
import { UPLOAD_MODE, toUploadResults, uniqueEmailsTest } from './contacts-import';
import { UploadModeSection } from './components/upload-mode-section';
import { ReplaceConfirmModal } from './components/replace-confirm-modal';

export const UploadContext = createContext();

const propertyOptions = {
  firstName: 'First Name',
  lastName: 'Last Name',
  email: 'Email',
  phone: 'Phone',
  pager: 'Pager',
  cisco: 'Cisco',
  department: 'Department',
  location: 'Location',
};

// The fuzzy column match maps a bare "Name" header to lastName.
const headerAliases = { name: 'firstName' };

const filterEmptyValues = val => (Array.isArray(val) ? val.filter(item => item !== '') : val);

// A mapped column stays in the row even when the cell is blank, so a blank clears the field
// like every other column does.
const trimLocations = contact =>
  Array.isArray(contact.location)
    ? { ...contact, location: contact.location.filter(loc => loc && loc.trim() !== '') }
    : contact;

const buildValidationSchema = (allowedDepartmentCodes, duplicateEmailMessage) =>
  Yup.object().shape({
    contacts: Yup.array()
      .of(
        Yup.object().shape({
          firstName: Yup.string()
            .transform(value => (value || '').trim())
            .required('First name is required')
            .min(1, 'First name cannot be empty'),
          lastName: Yup.string()
            .transform(value => (value && value.trim() ? value.trim() : null))
            .nullable(),
          email: Yup.string()
            .transform(value => (value && value.trim() ? value.trim().toLowerCase() : null))
            .email('Invalid email format')
            .nullable(),
          phone: Yup.string()
            .transform(value => (value && value.toString().trim() ? value.toString().trim() : null))
            .nullable(),
          pager: Yup.string()
            .transform(value => (value && value.toString().trim() ? value.toString().trim() : null))
            .nullable(),
          cisco: Yup.string()
            .transform(value => (value && value.toString().trim() ? value.toString().trim() : null))
            .nullable(),
          department: Yup.string()
            .transform(value => (value && value.trim() ? value.trim() : null))
            .oneOf(
              [...allowedDepartmentCodes, null, ''],
              ({ value }) => `Invalid department: ${value}`,
            )
            .nullable(),
          location: Yup.array().of(Yup.string()).transform(filterEmptyValues).nullable(),
        }),
      )
      .test(uniqueEmailsTest(duplicateEmailMessage)),
  });

export const ContactsUploadProvider = ({ children }) => {
  const dispatch = useDispatch();
  const organizationId = useSelector(state => state.organization.id);
  const lang = useSelector(getLang('SETTINGS'));
  const [uploadMode, setUploadMode] = useState(null);
  const [replaceErrors, setReplaceErrors] = useState([]);
  const [pendingReplace, setPendingReplace] = useState(null);
  const [isReplacing, setIsReplacing] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Per-org department options from /profile-fields ({ value: code, label }).
  const { departmentOptions } = useProfileOptions({});

  const validationSchema = useMemo(
    () =>
      buildValidationSchema(
        departmentOptions.map(({ value }) => value),
        lang.DUPLICATE_EMAIL,
      ),
    [departmentOptions, lang.DUPLICATE_EMAIL],
  );

  // Get labels from Redux for location options
  const labelsState = useSelector(state => state.labels);
  const labelsList = useMemo(() => getLabelsList({ labels: labelsState }) || [], [labelsState]);

  // Build professional select options with fuzzy matching
  const professionalSelectOptions = useMemo(() => {
    // Create findMatch function for department
    const departmentLabels =
      departmentOptions?.reduce((obj, { value, label }) => {
        obj[value] = label;
        return obj;
      }, {}) || {};
    const departmentFind = findClosestMatchFromObject(departmentLabels);

    // Create findMatch function for location
    const locationLabels = labelsList.reduce((obj, label) => {
      obj[label.code] = label.name;
      return obj;
    }, {});
    const locationFind = labelsList.length > 0 ? findClosestMatchFromObject(locationLabels) : null;

    return {
      department: {
        findMatch: departmentFind,
        options: departmentOptions || [],
      },
      location: {
        findMatch: locationFind,
        options: labelsList.map(label => ({
          value: label.code,
          label: label.name,
        })),
        multiple: true,
      },
    };
  }, [departmentOptions, labelsList]);

  const { showResults, stage, ...rest } = useUpload({
    propertiesMap: professionalSelectOptions,
  });

  const chooseUploadMode = mode => {
    setUploadMode(mode);
    setReplaceErrors([]);
  };

  const runImport = (contacts, options) =>
    dispatch(importContacts({ contacts, organizationId, ...options }));

  // A Replace removes every contact the file does not match, so it runs as a dry run first and
  // only writes once the admin confirms the removal count.
  const onSubmit = async data => {
    setReplaceErrors([]);
    setIsSubmitting(true);
    try {
      const contacts = data.contacts.map(trimLocations);

      if (uploadMode === UPLOAD_MODE.REPLACE) {
        const preview = toUploadResults(await runImport(contacts, { replace: true, dryRun: true }));
        if (preview.failed.length > 0) {
          setReplaceErrors(preview.failed);
        } else {
          setPendingReplace({ contacts, removedCount: preview.removed.length });
        }
        return;
      }

      showResults(toUploadResults(await runImport(contacts)));
    } catch (error) {
      console.error('Import failed:', error);
      showResults(toUploadResults({ error: error.message || 'Upload failed. Please try again.' }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const confirmReplace = async () => {
    setIsReplacing(true);
    const result = await runImport(pendingReplace.contacts, { replace: true });
    setIsReplacing(false);
    setPendingReplace(null);
    showResults(toUploadResults(result));
  };

  return (
    <UploadContext.Provider
      value={{
        ...rest,
        stage,
        validationSchema,
        propertyOptions,
        onSubmit,
        hasLocations: labelsList.length > 0,
        mandatoryFields: ['firstName'], // firstName is required
        headerAliases,
        canSubmit: Boolean(uploadMode),
        CustomPropsRender: (
          <UploadModeSection
            value={uploadMode}
            onChange={chooseUploadMode}
            replaceErrors={replaceErrors}
            disabled={isSubmitting}
          />
        ),
      }}>
      {children}
      <ReplaceConfirmModal
        open={Boolean(pendingReplace)}
        removedCount={pendingReplace?.removedCount ?? 0}
        loading={isReplacing}
        onCancel={() => setPendingReplace(null)}
        onConfirm={confirmReplace}
      />
    </UploadContext.Provider>
  );
};
